import React from 'react';
import { useTranslation } from 'react-i18next';
import { IconCheckCircleOutline } from '../../assets/icons/IconCheckCircleOutline';
import { IconEnvelope } from '../../assets/icons/IconEnvelope';
import { TranslationKey } from '../../i18n/translationKeys';
import { useLanguageStore } from '../../stores/languageStore';
import { Tag } from '../../ui/Tag/Tag';
import { Tooltip } from '../../ui/Tooltip/Tooltip';
import { formatRelanceOrdinal } from '../../utils/relance/relanceOrdinal';
import { relanceRankTone } from '../../utils/relance/relanceRankTone';

interface RelanceRankTagProps {
    /** 1-based position of the reminder about to be sent, or of the one just sent. */
    rank: number;
    sent?: boolean;
    /** Spells the rank as postponed ("1re relance reportée"), for a snoozed relance. */
    postponed?: boolean;
    /**
     * `condensed` (default) shows the ordinal and an icon so the customer name keeps the room, the full
     * wording left to screen readers; `spelled` writes it out in the pill ("2e relance"); `outlined`
     * writes it out in a neutral hairline outline, for a list kept in muted text throughout.
     */
    variant?: 'condensed' | 'spelled' | 'outlined';
    testId?: string;
    className?: string;
}

export const RelanceRankTag: React.FC<RelanceRankTagProps> = ({ rank, sent = false, postponed = false, variant = 'condensed', testId, className }) => {
    const { t } = useTranslation();
    const lang = useLanguageStore(s => s.lang);
    const ordinal = formatRelanceOrdinal(rank, lang);
    const fullLabel = sent
        ? (variant === 'condensed' ? t(TranslationKey.Home.FollowUp.RelanceSent, { rank: ordinal }) : t(TranslationKey.Home.Agenda.Sent))
        : t(postponed ? TranslationKey.Home.FollowUp.RelanceRankPostponed : TranslationKey.Home.FollowUp.RelanceRank, { rank: ordinal });
    const tone = variant === 'outlined' ? 'neutral' : sent ? 'success' : relanceRankTone(rank);

    return (
        <Tooltip content={t(TranslationKey.Home.FollowUp.RelanceRankHint)}>
            {describedById => (
                variant !== 'condensed' ? (
                    <Tag tone={tone} appearance={variant === 'outlined' ? 'outline' : 'pill'} testId={testId} aria-describedby={describedById} className={className}>
                        {fullLabel}
                    </Tag>
                ) : (
                    <Tag
                        tone={tone}
                        icon={sent
                            ? <IconCheckCircleOutline data-testid={testId ? `${testId}-sent-icon` : undefined} />
                            : <IconEnvelope data-testid={testId ? `${testId}-unsent-icon` : undefined} />}
                        iconPosition="end"
                        testId={testId}
                        aria-describedby={describedById}
                        className={className}
                    >
                        <span aria-hidden="true">{ordinal}</span>
                        <span className="sr-only">{fullLabel}</span>
                    </Tag>
                )
            )}
        </Tooltip>
    );
};
